/**
 * FYList.jsx — v4
 * CHANGES FROM v3:
 *   - FY creation removed entirely — handled by FYSetup (via SocietyDetail Overview tab)
 *   - This component is now a read-only list that navigates into a selected FY
 *   - Still shows all FYs with opening balances and status badges
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Calendar, ChevronRight } from 'lucide-react';
import { getFYs } from '../../api/yea.api';
import '../accounting/yea.css';

const FYList = ({ societyId, societyName }) => {
  const navigate = useNavigate();
  const [fys,     setFys]     = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFYs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getFYs(societyId);
      setFys(r.data.fys || r.data.financialYears || []);
    } catch { toast.error('Failed to load financial years'); }
    finally { setLoading(false); }
  }, [societyId]);

  useEffect(() => { fetchFYs(); }, [fetchFYs]);

  const statusColor = s => ({
    none:           'badge-subtle',
    pending_edit:   'badge-warning',
    pending_delete: 'badge-danger',
    approved_edit:  'badge-success',
    approved_delete:'badge-danger',
    rejected:       'badge-info',
  }[s] || 'badge-subtle');

  const statusLabel = s => ({
    none:           '',
    pending_edit:   'Edit Pending',
    pending_delete: 'Delete Pending',
    approved_edit:  'Edit Approved',
    approved_delete:'Deleted',
    rejected:       'Rejected',
  }[s] || '');

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <h2>Financial Years</h2>
          <p className="text-sm text-muted" style={{ marginTop:'0.2rem' }}>
            {societyName} — Select a year to view transactions
          </p>
        </div>
      </div>

      {fys.length === 0 ? (
        <div className="empty-state card">
          <Calendar size={40} strokeWidth={1} />
          <h3>No Financial Years</h3>
          <p>Go to the Overview tab to create a Financial Year</p>
        </div>
      ) : (
        <div className="fy-grid">
          {fys.map(fy => (
            <div key={fy._id} className="fy-card"
              onClick={() => navigate(`/dashboard/society/${societyId}/fy/${fy._id}`)}>
              {fy.approvalStatus !== 'none' && statusLabel(fy.approvalStatus) && (
                <div className="fy-pending-badge">
                  <span className={`badge ${statusColor(fy.approvalStatus)}`}>
                    {statusLabel(fy.approvalStatus)}
                  </span>
                </div>
              )}
              <div className="fy-card-name">{fy.fyName}</div>
              <div className="fy-card-dates">
                <Calendar size={11} />
                {new Date(fy.startDate).toLocaleDateString('en-IN')} — {new Date(fy.endDate).toLocaleDateString('en-IN')}
              </div>
              <div className="fy-card-balances">
                <div className="fy-balance-item">
                  <div className="fy-balance-label">Cash Opening</div>
                  <div className="fy-balance-val">₹{(fy.cashOpeningBalance||0).toLocaleString('en-IN')}</div>
                </div>
                <div className="fy-balance-item">
                  <div className="fy-balance-label">Banks</div>
                  <div className="fy-balance-val">{fy.bankOpeningBalances?.length || 0}</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', marginTop:'0.75rem', paddingTop:'0.75rem', borderTop:'1px solid var(--border-subtle)' }}>
                <span style={{ fontSize:'0.75rem', color:'var(--gold)', display:'flex', alignItems:'center', gap:'0.2rem' }}>
                  Open <ChevronRight size={12}/>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FYList;
